import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RotateCcw, X } from "lucide-react";
import "./Dashboard.css";

// AGENT-LINK FILTERING SUPPORT — same helper duplicated in every other
// dashboard file in this folder. Calendar marks aren't property/agent
// scoped (there's nothing to filter by — a long weekend is a company-wide
// fact, not tied to a specific property), so this is only used to carry
// the logged-in username through for "who last updated this date"
// bookkeeping, not to restrict what anyone sees.
function apiUrl(path) {
  let username = "";
  try {
    username = localStorage.getItem("loggedInUser") || "";
  } catch {
    username = "";
  }
  if (!username) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}username=${encodeURIComponent(username)}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dateStr(year, month, day) {
  // month is 0-indexed here (JS Date convention), matching how this file
  // tracks viewedYear/viewedMonth throughout.
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

function getTodayParts() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Friday, Saturday, Sunday are always shown green by default across the
// ENTIRE calendar, every month, with no manual marking needed — a pure
// day-of-week rule, not something stored in the backend. The manual
// "Mark as Long Weekend" toggle (still backend-persisted) stays useful
// for extending a weekend into an actual long weekend around a holiday —
// e.g. marking a Monday green too when Tuesday is a public holiday.
function isDefaultWeekend(dayOfWeek) {
  return dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6; // Sun=0, Fri=5, Sat=6
}

// Builds the full grid of cells for one month, including the leading/
// trailing blanks needed to keep every week row a full 7 cells (Sunday
// first, matching WEEKDAY_LABELS above).
function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay(); // 0 = Sunday
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null); // leading blanks
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null); // trailing blanks

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function DayEditModal({ date, mark, onSave, onClose }) {
  const [marked, setMarked] = useState(!!mark?.marked);
  const [comment, setComment] = useState(mark?.comment || "");
  const [saving, setSaving] = useState(false);

  if (!date) return null;

  const parsedDate = new Date(`${date}T00:00:00`);
  const prettyDate = parsedDate.toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  const isWeekendByDefault = isDefaultWeekend(parsedDate.getDay());

  async function handleSave() {
    setSaving(true);
    await onSave(date, { marked, comment });
    setSaving(false);
    onClose();
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff", borderRadius: 8, padding: 20, width: "90%",
          maxWidth: 420, boxShadow: "0 10px 40px rgba(0, 0, 0, 0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, color: "#073b4c", fontSize: 16 }}>{prettyDate}</h3>
            {isWeekendByDefault && (
              <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>
                Already shown green as a default weekend day
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", fontSize: 22, lineHeight: 1, cursor: "pointer", color: "#333" }}
          >
            <X size={18} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => setMarked((v) => !v)}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 8,
            border: marked ? "1px solid #16a34a" : "1px solid #cbd5e1",
            background: marked ? "#dcfce7" : "#f8fafc",
            color: marked ? "#166534" : "#334155",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            marginBottom: 16,
          }}
        >
          {marked ? "✓ Marked as Long Weekend" : "Mark as Long Weekend"}
        </button>

        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
          COMMENT
        </label>
        <textarea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a note for this date..."
          style={{
            width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e2e8f0",
            fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
          }}
        />

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 14px", borderRadius: 6, border: "1px solid #cbd5e1",
              background: "#fff", color: "#334155", fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 14px", borderRadius: 6, border: "none",
              background: "#073b4c", color: "#fff", fontSize: 13, fontWeight: 600,
              cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Calendar() {
  const today = useMemo(() => getTodayParts(), []);
  const [viewedYear, setViewedYear] = useState(today.year);
  const [viewedMonth, setViewedMonth] = useState(today.month); // 0-indexed
  const [marksByDate, setMarksByDate] = useState({}); // { "YYYY-MM-DD": { marked, comment } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingDate, setEditingDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl("/api/calendar/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        if (json.success) {
          const byDate = {};
          for (const item of json.data || []) {
            byDate[item.date] = { marked: !!item.marked, comment: item.comment || "" };
          }
          setMarksByDate(byDate);
        } else {
          setError(json.error || "Failed to load calendar data");
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const weeks = useMemo(() => buildMonthGrid(viewedYear, viewedMonth), [viewedYear, viewedMonth]);

  function goToPrevMonth() {
    setViewedMonth((m) => {
      if (m === 0) { setViewedYear((y) => y - 1); return 11; }
      return m - 1;
    });
  }
  function goToNextMonth() {
    setViewedMonth((m) => {
      if (m === 11) { setViewedYear((y) => y + 1); return 0; }
      return m + 1;
    });
  }
  function goToToday() {
    setViewedYear(today.year);
    setViewedMonth(today.month);
  }

  // Persists a change to the backend, then applies it locally so the grid
  // reflects it immediately. On failure, rolls back to whatever was there
  // before and alerts — same robust pattern Task.jsx uses, after the bug
  // we found there from NOT checking the response.
  async function saveMark(date, { marked, comment }) {
    const previous = marksByDate[date];
    setMarksByDate((prev) => ({ ...prev, [date]: { marked, comment } }));

    let username = "";
    try { username = localStorage.getItem("loggedInUser") || ""; } catch { /* ignore */ }

    try {
      const res = await fetch(apiUrl("/api/calendar/update"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, marked, comment, username }),
      });
      const json = await res.json();
      if (!json.success) {
        setMarksByDate((prev) => ({ ...prev, [date]: previous }));
        alert(json.error || "Couldn't save this date — please try again.");
      }
    } catch {
      setMarksByDate((prev) => ({ ...prev, [date]: previous }));
      alert("Couldn't save this date — please check your connection and try again.");
    }
  }

  if (loading) return <div className="rd-blank-tab">Loading calendar...</div>;
  if (error) return <div className="rd-blank-tab">Couldn't load calendar: {error}</div>;

  const monthLabel = new Date(viewedYear, viewedMonth, 1).toLocaleDateString("en-GB", {
    month: "long", year: "numeric",
  });

  return (
    <>
      <div className="rd-filter-bar occ-filter-bar">
        <div className="rd-filter-row cal-nav-row" style={{ alignItems: "center" }}>
          <button
            type="button"
            onClick={goToPrevMonth}
            className="rd-dropdown-btn"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, padding: 0 }}
            title="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="cal-month-label" style={{ fontSize: 18, fontWeight: 700, color: "#073b4c", minWidth: 180, textAlign: "center" }}>
            {monthLabel}
          </div>
          <button
            type="button"
            onClick={goToNextMonth}
            className="rd-dropdown-btn"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, padding: 0 }}
            title="Next month"
          >
            <ChevronRight size={16} />
          </button>
          <button onClick={goToToday} className="occ-btn-today" title="Jump to current month">
            <RotateCcw size={12} />
            TODAY
          </button>

          <div className="cal-legend" style={{ display: "flex", alignItems: "center", gap: 16, marginLeft: "auto", fontSize: 12, color: "#64748b" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: "#dcfce7", border: "1px solid #16a34a", display: "inline-block" }} />
              Weekend / Long Weekend
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, border: "2px solid #2563eb", display: "inline-block" }} />
              Today
            </span>
          </div>
        </div>
      </div>

      <div className="rd-matrix-section">
        <div className="rd-matrix-card cal-card" style={{ padding: 16 }}>
          <div className="cal-grid" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="cal-weekday" style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "#64748b", padding: "4px 0" }}>
                {label}
              </div>
            ))}

            {weeks.map((week, wIdx) =>
              week.map((day, dIdx) => {
                if (day === null) {
                  return <div key={`${wIdx}-${dIdx}`} className="cal-day-empty" style={{ minHeight: 92 }} />;
                }
                const d = dateStr(viewedYear, viewedMonth, day);
                const mark = marksByDate[d];
                const isToday = viewedYear === today.year && viewedMonth === today.month && day === today.day;
                const dayOfWeek = new Date(viewedYear, viewedMonth, day).getDay();
                const isMarked = !!mark?.marked || isDefaultWeekend(dayOfWeek);
                const hasComment = !!(mark?.comment && mark.comment.trim());

                return (
                  <button
                    key={`${wIdx}-${dIdx}`}
                    type="button"
                    onClick={() => setEditingDate(d)}
                    className="cal-day"
                    style={{
                      minHeight: 92,
                      textAlign: "left",
                      padding: 8,
                      borderRadius: 8,
                      border: isToday ? "2px solid #2563eb" : "1px solid #e2e8f0",
                      background: isMarked ? "#dcfce7" : "#fff",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: isToday ? 800 : 600, color: isToday ? "#1d4ed8" : "#1e293b" }}>
                      {day}
                    </span>
                    {hasComment && (
                      <span
                        className="cal-day-comment"
                        style={{
                          fontSize: 11,
                          color: "#475569",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.3,
                        }}
                      >
                        {mark.comment}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <DayEditModal
        date={editingDate}
        mark={editingDate ? marksByDate[editingDate] : null}
        onSave={saveMark}
        onClose={() => setEditingDate(null)}
      />
    </>
  );
}
